import { declined, RuleError, type Declined } from './ruleError';
import { boxInSet, sameSize } from './fromSet';
import { useManualSets } from './hardwareSets';
import { usePartLabel } from './partLabel';
import { useAssemblySlots } from './assemblySlots';
import { useMemo } from 'react';
import { qRotate, quatFromEulerDeg, type Vec3 } from './assemblyPose';
import type { Copy } from './assemblyStore';
import { useAssemblySteps } from './categoryBands';
import { shapeEdits } from './fittingModels';
import { crossBoreOf, fitAxisOf, useHoleFit } from './holeFit';
import { useMeshOf } from './holeGroups';
import { keptHand, productMiddleX, swapHand } from './holeLines';
import { acrossBore, acrossOf, holeMovement, unthreaded, type HoleMovement } from './jointMoves';
import { loadDeps } from './meshScene';
import { builtBoundsOf } from './openaiBlenderApi';
import { faceNormal, pointOnFace } from './partFaces';
import { loadSolid } from './partSolids';
import { pieceMiddles } from './pieceMiddle';
import { coverMarks, firstContact, isEntry } from './covers';
import { laysCovers, useJointCoverFacts } from './CoversRun';
import { isHardwareOrTool, isLeftRight, usePartsRun } from './partsRun';
import { fastenerKind, holePlace, type StepHole } from './stepHoles';
import { useStepHoles } from './stepHolesRun';
import { pieceContents, pieceItemId, useStepsRun, type SubStep } from './stepsRun';
import { pieceOf, type Job3D } from './threeDApi';

// A PIECE OF HARDWARE PUT INTO ITS HOLE, PLACED AS THE HOLE HAS IT.
//
// "Insert the barrel nut into the first side hole in the side rail": Find holes already says which
// hole, on which part, by which face, how deep the wood begins — and holeMovement turns that into
// where the nut rests, which way it comes in and how far out it starts. What is new here is WHAT IT
// IS PLACED ON: not the rail alone but
// the piece the sub-steps before have made, the rail already against the frame — a baked mesh whose
// middle is neither part's. So the hole's place is carried from its part to that piece: every part
// in it stands where the product built it, and the piece's own middle is read off its mesh.
//
// ONLY WHERE ONE HOLE ANSWERS: filed at this sub-step, for the hardware it adds — itself, or inside a
// piece made earlier (a screw with its washer on) — on a part that is in what it receives. Or,
// where none or several are filed at it, the hole whose TURN it is (see `byTurn`).
//
// ON EITHER HAND OF A PAIR. A hole in the other hand is said in the kept file's words (see
// holesOfLine), and the unit it is in is that file reflected across the product's middle — so its
// place, its face and what crosses it are reflected, and the hardware is then stood in THAT hole:
// worked out again on the reflected hole, not a movement reflected, which would be a screw with
// a left-hand thread. Anything else keeps the model's reading.

export type HolePlacement = {
  // The hole it goes into — null for a cover, which goes ON one (or under a foot): see `cover`.
  h: StepHole | null;
  // A COVER — a wood button over a screw, a felt pad under a foot.
  cover: boolean;
  // The movement, worked out on the press — its models are loaded for it. `modelOf`: the phase's
  // own drawable parts, pieces included (see useDrawableParts). With `copies` where the gesture
  // puts several units of a cover on, one each on its own place (see Fitting.copies).
  place: (modelOf: Map<number, Job3D>) => Promise<HoleMovement & { copies?: Copy[] }>;
};

const centre = (b: { min: number[]; max: number[] }): Vec3 =>
  [(b.min[0] + b.max[0]) / 2, (b.min[1] + b.max[1]) / 2, (b.min[2] + b.max[2]) / 2];

// Null where the sub-step is no hole's to place: nothing it adds is hardware or a cover. Declined,
// and why, where it is one and no hole answers.
//
// `prepared`: THE HARDWARE PREPARED INTO THE PART THE SUB-STEP BRINGS, instead of what it brings —
// the barrel nuts put into the side rail before the rail goes against the frame, one gesture
// (see Fitting.prepOn). Placed on that part, in its holes, as a sub-step of its own would be.
// `next`: THE SECOND HARDWARE IT PUTS INTO THE SAME PART, after the first — the wood dowels into
// the rail end its barrel nut has just gone into (see Fitting.nextOn). `last`: THE THIRD, after
// those two — the wood button over the JCBC screw just driven into its barrel nut (see Fitting.lastOn).
export type HoleMode = 'main' | 'prepared' | 'next' | 'last';
export function useHolePlacement(
  docId: string, slotKey: string | null | undefined, mode: HoleMode = 'main',
): HolePlacement | Declined | null {
  const { steps } = useAssemblySteps(docId);
  const { subSteps } = useStepsRun(docId);
  const { items } = usePartsRun(docId);
  const { byStep } = useStepHoles(docId);
  const meshOf = useMeshOf(docId);
  const fitOf = useHoleFit(docId);
  const coverUnits = useJointCoverFacts(docId);
  const label = usePartLabel(docId);
  const slots = useAssemblySlots(docId);
  const leaderOf = (key: string) => slots.find((s) => s.key === key)?.repeat?.leader ?? key;
  const sets = useManualSets(docId);
  return useMemo(() => {
    const [category, letter] = (slotKey ?? '').split('#');
    const sub = (subSteps[category] ?? []).find((s) => s.letter === letter);
    const hardware = (id: string) => !!items[Number(id)] && isHardwareOrTool(items[Number(id)]);
    // THE TWO ENDS OF WHAT A SUB-STEP PUTS IN. Bar the hardware prepared on the added part (a washer
    // on the screw): it rides the screw and is placed with it (see Fitting.prepOn), not a third end
    // of the fitting. Or, `into`, that hardware alone, put into the added part where that is a
    // component part — the barrel nuts into the rail: null where it is not.
    // AND OF TWO HARDWARE PUT INTO ONE PART IN TURN, the first, or `next` the second.
    const endsOf = (o: SubStep, as: HoleMode): SubStep['parts'] | null => {
      const all = o.parts ?? [];
      const said = all.filter((p) => p.role !== 'prepared');
      // AND OF THREE, the third (`last`).
      const two = said.filter((p) => p.role === 'added');
      const turn = (two.length === 2 || two.length === 3) && two.every((p) => hardware(p.id));
      const only = (k: number) => said.filter((p) => p.role !== 'added' || p === two[k]);
      if (as === 'last') return turn && two.length === 3 ? only(2) : null;
      if (as === 'next') return turn ? only(1) : null;
      if (as === 'main') return turn ? only(0) : said;
      const prep = all.find((p) => p.role === 'prepared');
      const part = all.find((p) => p.role === 'added');
      if (!prep || !part || !items[Number(part.id)] || hardware(part.id)) return null;
      return [{ ...prep, role: 'added' }, { ...part, role: 'receiving' }];
    };
    const parts = sub ? endsOf(sub, mode) : mode !== 'main' ? null : [];
    if (!parts) return null;
    const target = parts.find((p) => p.role === 'receiving')?.id;
    const added = parts.find((p) => p.role === 'added')?.id;
    const contents = pieceContents(steps, subSteps);
    // NOT A HOLE'S: what is added is a component part that covers nothing, or a piece holding no
    // hardware (the joint's).
    const covers = (id: string) => coverUnits.some((u) => u.rows.some((r) => r.covers.includes(id)));
    if (added != null && !(items[Number(added)] ? hardware(added) || covers(added) : (contents.get(added) ?? []).some(hardware))) {
      return null;
    }
    if (parts.length !== 2 || target == null || added == null) return declined('notice.rule.noRoles');
    // WHAT IS ADDED: a piece of hardware of the parts list, or a piece made earlier that carries one
    // — a screw with its washer on. The hole is the hardware's either way; a piece is placed by it.
    const piece = items[Number(added)] ? null : pieceItemId(added);
    const carried = piece == null ? [added] : contents.get(added) ?? [];
    const vias = carried.filter((id) => {
      const part = items[Number(id)];
      return !!part && isHardwareOrTool(part) && !!fastenerKind(part);
    });
    // OR A COVER: no hole is filed for it — it goes on the holes of the joint it closes, or under
    // the feet of the parts it is laid on (see coverOf).
    //
    // EVERY READING OF IT THIS SUB-STEP IS IN, not the first: four felt pads under the feet of two
    // frames are two readings, one per frame, and the first alone put two pads on and left two feet bare.
    const units = vias.length || piece != null ? [] : coverUnits.filter((u) => steps[u.joint.i]?.category === category
      && u.joint.group.some((s) => s.letter === letter) && u.rows.some((r) => r.covers.includes(added)));
    const unit = units[0] ?? null;
    if (!vias.length && !unit) return declined('notice.rule.hole.notCovered', { part: label(added) });
    const inside = (id: string) => id === target || (contents.get(target) ?? []).includes(id);
    const mx = productMiddleX(items, meshOf);
    // WHETHER A HOLE IS IN THE OTHER HAND of a pair — the unit that is the kept file reflected.
    // Null where it is in a pair and there is no middle to reflect across.
    const otherHand = (h: StepHole): boolean | null => {
      if (!h.side || !isLeftRight(items[Number(h.part)])) return false;
      const box = builtBoundsOf(meshOf(h.part));
      return box && mx != null ? keptHand(box, mx) !== h.side : null;
    };
    const subs = subSteps[category] ?? [];
    const holes = byStep[category]?.holes ?? [];
    // A screw's line is filed whole at the sub-step that drives it: the hole it ENTERS by is the one.
    const lineHead = (h: StepHole) => h.line == null || h.line === h.id;
    // BUT HARDWARE THAT SITS IN ITS HOLE goes into whichever end the gesture puts it in, not the end
    // its line starts at. A dowel's line ran from the side rail to the front legs frame, and Phase 4
    // put the dowels into the frame first: every line started in the rail, and none was placed.
    const seated = vias.every((id) => fastenerKind(items[Number(id)]) === 'seated');
    const mineOf = (list: StepHole[]) => {
      if (!seated) return list.some(lineHead) ? list.filter(lineHead) : list;
      const lines = new Map<string, StepHole[]>();
      list.forEach((h) => lines.set(h.line ?? h.id, [...(lines.get(h.line ?? h.id) ?? []), h]));
      return [...lines.values()].map((g) => g.find((h) => inside(h.part)) ?? g.find(lineHead) ?? g[0]);
    };
    // ON THE SIDE THE SUB-STEP WORKS ON, where it names one (the unit it goes into, else the one it
    // brings): a pair's holes are one per unit (StepHole.side), and "the second hole in the left
    // rail" put its barrel nut in the right one, the holes being sorted by sub-steps of an older
    // numbering. A hole of no side is either unit's.
    const sideOf = (ends: SubStep['parts']) => ends.find((u) => u.role === 'receiving')?.side
      ?? ends.find((u) => u.role === 'added')?.side;
    const side = sideOf(parts);
    // A line's side is its holes': the screw's entry in the frame has none, the hole it meets in the
    // rail has one.
    const sideOfHole = (h: StepHole) => h.side ?? holes.find((o) => o.line === (h.line ?? h.id) && o.side)?.side;
    const onSide = (h: StepHole) => !side || !sideOfHole(h) || sideOfHole(h) === side;
    const forVias = holes.filter((h) => h.via != null && vias.includes(h.via) && onSide(h));
    const filed = mineOf(forVias.filter((h) => h.at === letter && inside(h.part)));
    // BY TURN, where the sub-step it is filed at does not single one out. A barrel nut goes in
    // before the screw that meets it, at a sub-step its screw's row does not list — so the four
    // nuts of two rails were filed two at their own sub-steps and two at the first. What does not
    // move is the order: the n-th gesture that puts this hardware into a part takes the n-th of
    // its holes, in the order their lines are driven.
    // HOW MANY UNITS A GESTURE PUTS IN: its added entry's "qty" — two dowels pushed into one end of a
    // rail in one sub-step (see the step prompt's run of hand-pushed hardware). One each otherwise.
    const qtyOf = (ends: SubStep['parts']) => Math.max(1, ends.find((u) => u.role === 'added')?.qty ?? 1);
    const qty = qtyOf(parts);
    const byTurn = (): StepHole[] | null => {
      const order = (at: string) => subs.findIndex((o) => o.letter === at);
      const puts = (said: SubStep['parts']) => {
        const onto = said.find((u) => u.role === 'receiving')?.id;
        // NOT A PIECE WITH A COMPONENT PART IN IT: the rail with its first nut, brought onto the frame,
        // puts no nut into anything, and counted it made the left rail's second nut its third.
        const component = (id: string) => !!items[Number(id)] && !isHardwareOrTool(items[Number(id)]);
        const brings = said.filter((u) => u.role === 'added').flatMap((u) => {
          const held = contents.get(u.id) ?? [u.id];
          return held.some(component) ? [] : held;
        });
        // Onto a part or a piece — a washer slid onto its screw puts nothing into a hole.
        return said.length === 2 && onto != null && !(items[Number(onto)] && isHardwareOrTool(items[Number(onto)]))
          && brings.some((id) => vias.includes(id));
      };
      // Counted among the gestures on the same side: the left rail's second nut is the second
      // left-hand one, whatever went into the right rail in between.
      // And by UNIT: a gesture putting two in takes two holes, and the one after it starts past them.
      // Each sub-step's hardware prepared into its part first, then what it puts in itself, first and
      // second.
      const modes: HoleMode[] = ['prepared', 'main', 'next', 'last'];
      const mine = subs.flatMap((o) => modes.map((as) => ({ o, as, ends: endsOf(o, as) })))
        .filter((g): g is { o: SubStep; as: HoleMode; ends: SubStep['parts'] } =>
          !!g.ends && puts(g.ends) && (!side || sideOf(g.ends) === side));
      const turn = mine.findIndex((g) => g.o.letter === letter && g.as === mode);
      const from = mine.slice(0, Math.max(0, turn)).reduce((sum, g) => sum + qtyOf(g.ends), 0);
      const driven = (h: StepHole) => order((holes.find((o) => o.id === h.line) ?? h).at);
      const pool = mineOf(forVias).sort((a, b) => driven(a) - driven(b)
        || a.id.localeCompare(b.id, undefined, { numeric: true }));
      const taken = turn < 0 ? [] : pool.slice(from, from + qty);
      return taken.length === qty && taken.every((h) => inside(h.part)) ? taken : null;
    };
    const into = unit ? null : filed.length === qty ? filed : byTurn();
    const hole = into?.[0] ?? null;
    // WHY NONE, as it is: nothing filed for it, nothing filed in this part, or too few left for it.
    if (!unit && !hole) {
      const filedFor = mineOf(forVias);
      const filedIn = filedFor.filter((h) => inside(h.part));
      const args = { part: label(vias[0]), onto: label(target), n: filedIn.length || filedFor.length, qty };
      return declined(!filedFor.length ? 'notice.rule.hole.noneFiled'
        : !filedIn.length ? 'notice.rule.hole.noneInPart' : 'notice.rule.hole.noneLeft', args);
    }
    // THE PIECE IT RUNS ACROSS (a barrel nut across its screw's line), which says how it is turned.
    const crossedOf = (h: StepHole) => (h.line && h.line !== h.id ? holes.find((o) => o.id === h.line && o.via !== h.via) : undefined);

    // THE COVER THIS SUB-STEP PUTS ON, and where — BY RULE, no call. A button goes on every hole a
    // screw it caps ENTERS by (through, its head in it); a pad on every foot of the parts it is laid
    // under. Which way up is its model's (see builtDown), and it rests where it first touches. A
    // model was asked this, off the same holes and feet, and answered exactly that every time.
    //
    // The joint's covers are dealt to its sub-steps in order — buttons in the order of the screws
    // they cap, pads in the order of the feet (as the joint view dealt them out, see dealCovers) —
    // AS MANY TO EACH AS IT PUTS ON: a sub-step putting four pads on takes four feet.
    const coverOf = (u: NonNullable<typeof unit>): StepHole[] => {
      const rows = u.rows.filter((r) => r.covers.includes(added));
      const laid = rows.filter(laysCovers);
      const capped = new Set(rows.flatMap((r) => r.hardware.filter((id) => !r.covers.includes(id))));
      const on = laid.length
        ? u.spots.filter((s) => laid.some((r) => r.components.includes(s.part))).map((s) => s.id)
        : u.holes.filter((o) => isEntry(o) && o.through && o.via != null && capped.has(o.via)).map((o) => o.id);
      const lastTo = (m: StepHole) => {
        const k = u.joint.capped?.group.findIndex((sub) => sub.letter === m.at) ?? -1;
        return k < 0 ? 1e6 : k;
      };
      const marks = coverMarks(on.map((id) => ({ cover: added, on: id, flip: false })), u.holes, u.spots)
        .sort((a, b) => lastTo(a) - lastTo(b) || a.id.localeCompare(b.id, undefined, { numeric: true }));
      const mine = dealCovers(u.joint.group, added, marks.length)[u.joint.group.findIndex((s) => s.letter === letter)] ?? [];
      const dealt = mine.map((n) => marks[n]);
      if (!dealt.length) throw new RuleError('notice.rule.noCoverPlace');
      const loose = dealt.find((m) => !inside(m.part));
      if (loose) throw new RuleError('notice.rule.markNotHeld', { id: loose.id });
      return dealt;
    };

    // WHERE WHAT IT IS PLACED ON HAS ITS MIDDLE, in the product's frame — see pieceMiddle.
    const middleOf = pieceMiddles({ docId, steps, subSteps, items, meshOf, label, leaderOf });

    // WHERE A SCREW'S THREAD BITES, of its whole way in: it passes clear through the holes it only
    // crosses and turns where it is wound into something. Into a nut across its line (a barrel nut),
    // the nut's thickness, its smallest side; else into the blind hole its line ends in, that hole's
    // depth. A JCBC screw turned seventy-one times through a frame it only passes, for the ten
    // millimetres of barrel nut it actually screws into. Null where neither is known.
    const biteOf = (h: StepHole): number | null => {
      const line = holes.filter((o) => o.id === h.id || o.line === h.id);
      const nut = line.find((o) => o.via != null && o.via !== h.via);
      const nutFit = nut && fitOf(nut);
      if (nutFit) {
        const b = nutFit.built;
        const sides = b ? [b.x, b.y, b.z] : nutFit.dims ? [nutFit.dims.x, nutFit.dims.y, nutFit.dims.z] : [];
        const thin = Math.min(...sides.filter((v): v is number => typeof v === 'number' && v > 0));
        if (Number.isFinite(thin)) return thin;
      }
      const blind = line.filter((o) => o.via === h.via && !o.through && o.depthMm != null);
      return blind.length ? blind[blind.length - 1].depthMm! : null;
    };

    // ONE UNIT STOOD IN ITS PLACE, seated on what it is fitted onto.
    const placeOne = async (h: StepHole, modelOf: Map<number, Job3D>): Promise<HoleMovement> => {
      const moving = items[Number(h.via)];
      const job = meshOf(h.part);
      const box = builtBoundsOf(job);
      const fit = fitOf(h);
      if (!job || !box || !fit) throw new RuleError('notice.rule.noModelUnder', { id: h.id });
      if (!unit && h.surfaceMm == null) throw new RuleError('notice.rule.noSurface', { id: h.id });
      const { THREE, GLTFLoader } = await loadDeps();
      const loader = new GLTFLoader();
      const axis = await fitAxisOf(THREE, loader, fit).catch(() => null);
      if (!axis) throw new RuleError('notice.rule.hardwareModel', { id: h.id });
      if (unthreaded(axis, moving)) throw new RuleError('notice.rule.noThread', { id: h.id });
      const dims = { x: box.max[0] - box.min[0], y: box.max[1] - box.min[1], z: box.max[2] - box.min[2] };
      const at = holePlace(h, dims);
      const onFace = at && pointOnFace({ face: h.face, u: at.u, v: at.v }, dims);
      if (!onFace) throw new RuleError('notice.rule.offFace', { id: h.id });
      // IN THE OTHER HAND: everything below is first read on the kept file, where the hole is said,
      // and then stood in the unit it is in — see `reflected`.
      const other = otherHand(h);
      if (other == null) throw new RuleError('notice.rule.noMiddle', { id: h.id });
      const reflected = <T extends { face: StepHole['face'] }>(o: T): T => ({ ...o, face: swapHand(o.face) });
      const crossed = crossedOf(h);
      const crossedFit = crossed && fitOf(crossed);
      const crossedAxis = crossedFit ? await fitAxisOf(THREE, loader, crossedFit).catch(() => null) : null;
      // What crosses it is said in its own part's words: reflected where that is the other hand too.
      const crossing = crossed && (otherHand(crossed) ? reflected(crossed) : crossed);
      const sameSet = crossing && crossedFit && crossedAxis ? acrossOf(fit, crossedFit, crossedAxis, crossing) : null;
      // Not built together: its bore, off its own mesh.
      const bore = crossing && !sameSet ? await crossBoreOf(THREE, loader, fit, axis.into).catch(() => null) : null;
      const across = sameSet ?? (crossing && bore ? acrossBore(bore, crossing) : null);
      // A PIECE THAT CARRIES THE HARDWARE — the screw with its washer on, built as one set. The
      // washer is under the head, so the screw stands out of the wood by its thickness (as the joint
      // view stands it); and what moves is the piece, whose middle is not the screw's: carried from
      // one to the other in the set's frame, which is the screw's own model's and the piece's too.
      let stood = h;
      let carry: Vec3 = [0, 0, 0];
      if (piece != null) {
        if (!modelOf.get(piece)) throw new RuleError('notice.rule.noPieceMoving', { id: label(added) });
        const own = builtBoundsOf(fit.job);
        const set = pieceOf(fit.job);
        const notOne = () => new RuleError('notice.rule.notOneSet', {
          id: label(added), parts: carried.map(label).join(' + '), hw: label(h.via!),
        });
        if (!own || set?.from !== 'set' || shapeEdits(fit.job) > 0) throw notOne();
        // EVERY OTHER PART OF THE PIECE, IN THE HARDWARE'S FRAME: its own box where it was cut from
        // the hardware's set; else its box carried over from ITS set, through that set's copy of the
        // hardware — the washer kept from "washer + screw", the screw from "Allen key + screw". Its
        // set says where it sits on its screw; moved from that screw's middle to the kept one's, it
        // is where it sits on this one. Only where the two screws are one size (see sameSize).
        const boxes = carried.filter((id) => id !== h.via).map((id) => {
          const job = meshOf(id);
          const b = builtBoundsOf(job);
          const of = pieceOf(job);
          if (!job || !b || of?.from !== 'set' || shapeEdits(job) > 0) throw notOne();
          if (of.of === set.of) return b;
          const theirs = boxInSet(sets.find((j) => j.id === of.of), fit.job);
          if (!theirs) throw new RuleError('notice.rule.setLacks', { part: label(id), hw: label(h.via!) });
          if (!sameSize(theirs, own)) throw new RuleError('notice.rule.setOtherHw', { part: label(id), hw: label(h.via!) });
          const [to, from] = [centre(own), centre(theirs)];
          const d = [0, 1, 2].map((i) => to[i] - from[i]);
          return { min: b.min.map((v, i) => v + d[i]), max: b.max.map((v, i) => v + d[i]) };
        });
        const all = [own, ...boxes];
        const whole = centre({
          min: [0, 1, 2].map((i) => Math.min(...all.map((b) => b.min[i]))),
          max: [0, 1, 2].map((i) => Math.max(...all.map((b) => b.max[i]))),
        });
        const mine0 = centre(own);
        carry = [whole[0] - mine0[0], whole[1] - mine0[1], whole[2] - mine0[2]];
        const lift = all.slice(1).reduce((sum, b) => sum
          + [0, 1, 2].reduce((along, i) => along + Math.abs(axis.into[i]) * (b.max[i] - b.min[i]), 0), 0);
        stood = { ...h, sunkMm: (h.sunkMm ?? 0) - lift };
      }
      // WHERE THE WOOD BEGINS UNDER IT: its hole's, as the line measured it; a cover's, where it
      // first touches — rays along its way in, across its width (see firstContact).
      let mouth = h.surfaceMm ?? 0;
      if (unit) {
        const solid = await loadSolid(job, items[Number(h.part)]?.size).catch(() => null);
        const c = centre(box);
        const hit = solid && firstContact(solid, [c[0] + onFace[0], c[1] + onFace[1], c[2] + onFace[2]], faceNormal(h.face), axis.reach);
        if (hit == null) throw new RuleError('notice.rule.nothingUnderCover', { id: h.id });
        mouth = Math.max(0, hit);
      }
      // THE OTHER HAND'S HOLE IS THE KEPT ONE'S MIRROR IMAGE: on the facing face, at the reflected
      // place, in a part standing at the reflected place. The hardware is worked out in THAT hole.
      const flip = (v: Vec3): Vec3 => (other ? [-v[0], v[1], v[2]] : v);
      const m = holeMovement(other ? reflected(stood) : stood, axis, flip(onFace), moving, mouth, across, biteOf(h));
      const turned = qRotate(quatFromEulerDeg(m.spin), carry);
      // From the hole's part to what is on screen: both middles in the product's frame.
      const kept = centre(box);
      const part: Vec3 = other ? [2 * mx! - kept[0], kept[1], kept[2]] : kept;
      // ON THE PART THE HOLE IS IN, where that is what it goes onto (the barrel nuts into the rail,
      // see `prepared`): that part's middle on ITS side. Its kept file's was the other rail's, and
      // the right rail's nuts stood the width of the chair away from it.
      const onto = target === h.part ? part : await middleOf(target, modelOf);
      const seat = (i: number) => Math.round((m.seat[i] + turned[i] + part[i] - onto[i]) * 10) / 10;
      return { ...m, seat: [seat(0), seat(1), seat(2)] };
    };

    // EVERY UNIT THE GESTURE PUTS ON: the hardware in its one hole, or each cover on its own place —
    // the first as the movement, the rest as its copies.
    const place = async (modelOf: Map<number, Job3D>): Promise<HoleMovement & { copies?: Copy[] }> => {
      const [first, ...rest] = unit ? units.flatMap(coverOf) : into!;
      const m = await placeOne(first, modelOf);
      if (!rest.length) return m;
      const others = await Promise.all(rest.map((h) => placeOne(h, modelOf)));
      return { ...m, copies: others.map((o) => ({ seat: o.seat, spin: o.spin, axis: o.axis, travel: o.travel })) };
    };
    return { h: hole, cover: !!unit, place };
  }, [docId, slotKey, mode, steps, subSteps, items, byStep, meshOf, fitOf, coverUnits, label, sets, slots]);
}

// WHICH OF A JOINT'S COVER PLACES EACH OF ITS SUB-STEPS TAKES — by the sub-step's position in
// `group`, as indices into the places in the order they are dealt: every sub-step putting `cover`
// on, in order, AS MANY AS IT PUTS ON — its "qty" — so a sub-step putting four pads on takes four
// feet and one putting one takes one. Where the places and the units do not count the same, each
// place goes to the unit at its share of the way through. Shared with the joint view (JointHoles),
// which must deal them the same way. By position and not by letter: a joint may run across steps,
// and letters start again in each.
export function dealCovers(group: SubStep[], cover: string, places: number): number[][] {
  const units = group.map((sub) => {
    const said = (sub.parts ?? []).find((p) => p.id === cover && (!p.role || p.role === 'added'));
    return said ? Math.max(1, said.qty ?? 1) : 0;
  });
  const total = units.reduce((sum, n) => sum + n, 0);
  const out: number[][] = group.map(() => []);
  for (let n = 0; n < places && total; n += 1) {
    let unitAt = Math.floor((n * total) / places);
    const to = units.findIndex((u) => u > 0 && (unitAt -= u) < 0);
    if (to >= 0) out[to].push(n);
  }
  return out;
}
